import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  INTAKE_STEP_COUNT,
  INTAKE_STEP_IDS,
  INTAKE_STEP_LABELS,
  clearRetryQueue,
  dropRetry,
  enqueueRetry,
  nextIntakeStep,
  prevIntakeStep,
  readDraft,
  readRetryQueue,
  reconcileCurrentStep,
  stepIdToNumber,
  writeDraft,
  type IntakeStepId,
} from '../../../lib/intake-draft.js';
import {
  IntakePortalNotFoundError,
  IntakeStepSubmissionError,
  IntakeSubmitError,
  submitIntakeForReview,
  submitIntakeStep,
  type IntakePortalState,
  type IntakeStepSubmissionIssue,
  type IntakeSubmitResponse,
} from '../../../lib/api.js';

/**
 * wd-2.3.1 — Prospect-facing intake portal shell. Renders the current
 * step based on the server-hydrated `TenantIntake` + the client draft;
 * handles auto-save (localStorage) + submit (POST /step) + offline
 * retry queue. The per-step body is plugged in via `renderStep` — the
 * real components (D2.3.2–D2.3.11) land in later tickets; this shell
 * ships with a deterministic placeholder renderer so the chrome can be
 * exercised end-to-end without the concrete forms.
 */

const AUTOSAVE_DEBOUNCE_MS = 500;

export type IntakeShellRenderProps = {
  stepId: IntakeStepId;
  stepDraft: Record<string, unknown>;
  onChange: (patch: Record<string, unknown>) => void;
  intake: IntakePortalState;
  saveState: SaveIndicator;
  issues: ReadonlyArray<IntakeStepSubmissionIssue>;
  /** Base API URL — needed by step components that POST directly (e.g. uploads). */
  apiUrl: string;
  /** Intake invite token — needed by step components that POST directly. */
  token: string;
  /**
   * Jump to another step. Used by Step 10's "Edit this section" deep-links
   * back to the owning step. Re-uses the shell's `goToStep` — locked steps
   * (above `max(intake.lastStep, currentN)`) are still clickable from this
   * route since the prospect has demonstrated progress on Step 10 already.
   */
  onGoToStep: (stepId: IntakeStepId) => void;
};

export type SaveIndicator =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: string }
  | { kind: 'offline' }
  | { kind: 'error'; message: string };

export type IntakeShellProps = {
  apiUrl: string;
  token: string;
  initialIntake: IntakePortalState;
  renderStep?: (props: IntakeShellRenderProps) => ReactNode;
  /**
   * Test seam. Defaults to the real `submitIntakeStep`. Override to
   * inject a stub without touching fetch globally.
   */
  submit?: typeof submitIntakeStep;
  /**
   * Test seam for the D2.3.12 "Submit for Review" call. Defaults to the
   * real `submitIntakeForReview`.
   */
  finalize?: typeof submitIntakeForReview;
  /** Test seam so the autosave debounce is observable. */
  debounceMs?: number;
  /** Test seam for navigator.onLine detection. */
  isOnline?: () => boolean;
};

export function defaultRenderStep({
  stepId,
  stepDraft,
  onChange,
  issues,
}: IntakeShellRenderProps): ReactNode {
  const label = INTAKE_STEP_LABELS[stepId];
  // Expose a single free-text "notes" field so the shell's autosave +
  // submit wiring is exercisable before the real step components land
  // (D2.3.2–D2.3.11). Real steps will replace this via `renderStep`.
  const value = typeof stepDraft.notes === 'string' ? stepDraft.notes : '';
  return (
    <div
      className="ferro-intake__placeholder"
      role="note"
      aria-label={`${label} step placeholder`}
    >
      <p className="ferro-muted">
        {label} — fields wire up in later tasks (D2.3.{stepIdToNumber(stepId) + 1}).
      </p>
      <label className="ferro-field">
        <span className="ferro-field__label">Notes</span>
        <input
          type="text"
          className="ferro-field__input"
          value={value}
          data-testid={`intake-step-${stepId}-notes`}
          onChange={(e) => onChange({ notes: e.target.value })}
        />
      </label>
      {issues.length > 0 ? (
        <ul className="ferro-intake__issues" role="alert">
          {issues.map((i) => (
            <li key={`${i.path}:${i.message}`}>
              <strong>{i.path || 'field'}</strong> — {i.message}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function stepKey(step: IntakeStepId): `step${number}` {
  return `step${stepIdToNumber(step)}` as `step${number}`;
}

function pickStepDraft(
  serverPayload: Record<string, unknown>,
  clientPayload: Record<string, unknown>,
  step: IntakeStepId,
): Record<string, unknown> {
  const key = stepKey(step);
  const fromClient = clientPayload[key];
  if (fromClient && typeof fromClient === 'object' && !Array.isArray(fromClient)) {
    return fromClient as Record<string, unknown>;
  }
  const fromServer = serverPayload[key];
  if (fromServer && typeof fromServer === 'object' && !Array.isArray(fromServer)) {
    return fromServer as Record<string, unknown>;
  }
  return {};
}

export function IntakeShell({
  apiUrl,
  token,
  initialIntake,
  renderStep = defaultRenderStep,
  submit = submitIntakeStep,
  finalize = submitIntakeForReview,
  debounceMs = AUTOSAVE_DEBOUNCE_MS,
  isOnline = defaultIsOnline,
}: IntakeShellProps): JSX.Element {
  const [intake, setIntake] = useState<IntakePortalState>(initialIntake);

  // Initial client state: server payload + any prior client draft that
  // was left behind by a crash/reload/tab-close.
  const [currentStep, setCurrentStep] = useState<IntakeStepId>(() => {
    const stored = readDraft(token);
    return reconcileCurrentStep(initialIntake.lastStep, stored);
  });
  const [draftPayload, setDraftPayload] = useState<Record<string, unknown>>(
    () => {
      const stored = readDraft(token);
      return stored ? stored.payload : {};
    },
  );
  const [saveState, setSaveState] = useState<SaveIndicator>({ kind: 'idle' });
  const [submitting, setSubmitting] = useState(false);
  const [issues, setIssues] = useState<ReadonlyArray<IntakeStepSubmissionIssue>>(
    [],
  );

  // Debounced localStorage autosave. Every field change flushes to the
  // client draft within `debounceMs`; the server isn't touched until
  // the user clicks "Save & continue" (§5.1 of the design doc requires
  // a dedicated autosave endpoint that skips schema validation — not
  // yet landed; draft-to-server sync lands with that endpoint).
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards the save indicator while a submit (or finalize) is mid-flight.
  // An autosave that fires inside that window would otherwise overwrite
  // an `error` or `saved` state the submit chain just committed — the
  // hash-computation useEffect at step 10 reliably schedules a debounce
  // on payload change, so this is not a theoretical race.
  const submittingRef = useRef(false);

  const scheduleAutosave = useCallback(
    (nextStep: IntakeStepId, nextPayload: Record<string, unknown>) => {
      if (autosaveTimer.current !== null) {
        clearTimeout(autosaveTimer.current);
      }
      // Don't clobber an explicit `error` indicator with a `saving`
      // flash — if the user is looking at a submission error, the
      // draft is still being persisted to localStorage but the
      // indicator should stay on the error until they act.
      if (!submittingRef.current) {
        setSaveState((prev) => (prev.kind === 'error' ? prev : { kind: 'saving' }));
      }
      autosaveTimer.current = setTimeout(() => {
        const saved = writeDraft({
          token,
          currentStep: nextStep,
          payload: nextPayload,
        });
        if (!submittingRef.current) {
          setSaveState((prev) =>
            prev.kind === 'error' ? prev : { kind: 'saved', at: saved.updatedAt },
          );
        }
        autosaveTimer.current = null;
      }, debounceMs);
    },
    [token, debounceMs],
  );

  // Cancel any pending autosave on unmount to avoid a setState after
  // the shell has gone away (React 19 strict-mode unmount).
  useEffect(() => {
    return () => {
      if (autosaveTimer.current !== null) {
        clearTimeout(autosaveTimer.current);
      }
    };
  }, []);

  const handleStepChange = useCallback(
    (patch: Record<string, unknown>) => {
      setDraftPayload((prev) => {
        const key = stepKey(currentStep);
        const priorStep =
          prev[key] && typeof prev[key] === 'object' && !Array.isArray(prev[key])
            ? (prev[key] as Record<string, unknown>)
            : {};
        const nextPayload = {
          ...prev,
          [key]: { ...priorStep, ...patch },
        };
        scheduleAutosave(currentStep, nextPayload);
        return nextPayload;
      });
      // Clear inline issues on edit; the user is fixing the field.
      if (issues.length > 0) setIssues([]);
    },
    [currentStep, scheduleAutosave, issues.length],
  );

  const goToStep = useCallback(
    (step: IntakeStepId) => {
      setCurrentStep(step);
      setIssues([]);
      // Persist navigation immediately so reload lands at the same step.
      const saved = writeDraft({ token, currentStep: step, payload: draftPayload });
      setSaveState({ kind: 'saved', at: saved.updatedAt });
    },
    [token, draftPayload],
  );

  const handleBack = useCallback(() => {
    const prev = prevIntakeStep(currentStep);
    if (prev) goToStep(prev);
  }, [currentStep, goToStep]);

  const handleSaveAndFinishLater = useCallback(() => {
    if (autosaveTimer.current !== null) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }
    const saved = writeDraft({ token, currentStep, payload: draftPayload });
    setSaveState({ kind: 'saved', at: saved.updatedAt });
  }, [token, currentStep, draftPayload]);

  const submitCurrentStep = useCallback(async () => {
    setIssues([]);
    const key = stepKey(currentStep);
    const payload = draftPayload[key] ?? {};

    // Offline: queue the submission; let the online handler flush later.
    if (!isOnline()) {
      enqueueRetry(token, { stepId: currentStep, payload });
      setSaveState({ kind: 'offline' });
      return;
    }

    // Cancel any pending autosave debounce before we start the
    // submit round-trip. The guard below keeps any later-scheduled
    // autosave from setting `saveState` while the submit chain owns
    // the indicator.
    if (autosaveTimer.current !== null) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }
    submittingRef.current = true;

    setSubmitting(true);
    setSaveState({ kind: 'saving' });
    try {
      const out = await submit(apiUrl, token, {
        stepId: currentStep,
        payload,
      });
      setIntake((prev) => ({
        ...prev,
        state: out.state,
        lastStep: out.lastStep,
        payload: { ...prev.payload, [key]: payload },
        updatedAt: out.updatedAt,
      }));
      setSaveState({ kind: 'saved', at: out.updatedAt });
      // Submission succeeded — drop any queued retry for this step.
      dropRetry(token, currentStep);

      // D2.3.12 — at step 10 the primary CTA is "Submit for Review".
      // Step 10 save just landed; now chain the finalize call that
      // transitions `in_progress → submitted` and fires the admin
      // notification. On success the shell pivots to the read-only
      // thank-you screen; on failure we surface the typed error below
      // without rolling back the step save.
      if (currentStep === '10') {
        try {
          const finalized: IntakeSubmitResponse = await finalize(
            apiUrl,
            token,
          );
          setIntake((prev) => ({
            ...prev,
            state: finalized.state,
            submittedAt: finalized.submittedAt,
            updatedAt: finalized.updatedAt,
          }));
          setSaveState({ kind: 'saved', at: finalized.updatedAt });
          return;
        } catch (err) {
          if (err instanceof IntakePortalNotFoundError) {
            setSaveState({
              kind: 'error',
              message: 'This intake link is no longer valid.',
            });
          } else if (err instanceof IntakeSubmitError) {
            setSaveState({
              kind: 'error',
              message: describeSubmitError(err),
            });
          } else {
            const message =
              err instanceof Error ? err.message : 'Submit failed';
            setSaveState({ kind: 'error', message });
          }
          return;
        }
      }

      // Advance if we're not at the final step.
      const next = nextIntakeStep(currentStep);
      if (next) goToStep(next);
    } catch (err) {
      if (err instanceof IntakePortalNotFoundError) {
        setSaveState({
          kind: 'error',
          message: 'This intake link is no longer valid.',
        });
      } else if (err instanceof IntakeStepSubmissionError) {
        if (err.kind === 'network') {
          enqueueRetry(token, { stepId: currentStep, payload });
          setSaveState({ kind: 'offline' });
        } else if (err.kind === 'validation') {
          setIssues(err.body?.issues ?? []);
          setSaveState({
            kind: 'error',
            message: 'Fix the highlighted fields and try again.',
          });
        } else {
          setSaveState({ kind: 'error', message: err.message });
        }
      } else {
        const message = err instanceof Error ? err.message : 'Submission failed';
        setSaveState({ kind: 'error', message });
      }
    } finally {
      // Discard any autosave debounce that got re-armed during the
      // submit round-trip (typically by Step 10's hash-computation
      // effect reacting to the setIntake update). Leaving it in
      // place would let a later `saved` indicator overwrite the
      // final error or thank-you state the submit chain just set.
      if (autosaveTimer.current !== null) {
        clearTimeout(autosaveTimer.current);
        autosaveTimer.current = null;
      }
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [
    apiUrl,
    token,
    currentStep,
    draftPayload,
    submit,
    finalize,
    isOnline,
    goToStep,
  ]);

  // Replay queued submissions when the tab comes back online.
  useEffect(() => {
    const flush = async (): Promise<void> => {
      if (!isOnline()) return;
      const queue = readRetryQueue(token);
      if (queue.entries.length === 0) return;
      for (const entry of queue.entries) {
        try {
          await submit(apiUrl, token, {
            stepId: entry.stepId,
            payload: entry.payload,
          });
          dropRetry(token, entry.stepId);
        } catch {
          // Leave in queue; next online event will try again.
          return;
        }
      }
      // If everything drained, reset the indicator.
      setSaveState({ kind: 'idle' });
      clearRetryQueue(token);
    };

    const handler = (): void => {
      void flush();
    };
    window.addEventListener('online', handler);
    // Attempt once on mount in case we booted with a queued payload.
    void flush();
    return () => {
      window.removeEventListener('online', handler);
    };
  }, [apiUrl, token, submit, isOnline]);

  const currentN = stepIdToNumber(currentStep);
  const atFirst = currentN === 1;
  const atLast = currentN === INTAKE_STEP_COUNT;
  const progressPct = useMemo(
    () => Math.round((currentN / INTAKE_STEP_COUNT) * 100),
    [currentN],
  );

  // D2.3.12 — once the intake is in `submitted` state (either the
  // prospect just submitted or they reloaded the tab afterwards),
  // lock the portal into a read-only thank-you screen. No way back
  // into the form from here; if the admin rejects the intake the
  // server flips state back to `rejected` → `in_progress` via D3.4.
  if (intake.state === 'submitted') {
    return renderSubmittedConfirmation(intake);
  }

  const shellRender = renderStep({
    stepId: currentStep,
    stepDraft: pickStepDraft(intake.payload, draftPayload, currentStep),
    onChange: handleStepChange,
    intake,
    saveState,
    issues,
    apiUrl,
    token,
    onGoToStep: goToStep,
  });

  return (
    <main className="ferro-intake" aria-label="Tenant intake portal">
      <header className="ferro-intake__header">
        <div className="ferro-intake__brand">
          <span className="ferro-intake__brandMark" aria-hidden="true">
            ALA
          </span>
          <span className="ferro-intake__brandLabel">Animal Lovers Platform</span>
        </div>
        <div className="ferro-intake__headerMeta">
          <p className="ferro-intake__headerTitle">
            Step {currentN} of {INTAKE_STEP_COUNT}
          </p>
          <p className="ferro-intake__headerSubtitle">
            {INTAKE_STEP_LABELS[currentStep]}
          </p>
        </div>
      </header>

      <div
        className="ferro-intake__progress"
        role="progressbar"
        aria-label="Intake progress"
        aria-valuenow={progressPct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="ferro-intake__progressFill"
          style={{ width: `${progressPct}%` }}
          data-testid="intake-progress-fill"
        />
        <span className="ferro-intake__progressLabel">{progressPct}% complete</span>
      </div>

      {/*
        wd-3.4.2 — Reviewer-feedback banner. The admin's rejection note
        lands on `intake.rejectionReason`; the banner renders while the
        intake is still in `rejected` state. First field edit flips the
        state back to `in_progress` via the submission service and the
        banner drops on the next response.
      */}
      {intake.state === 'rejected' ? (
        <aside
          className="ferro-intake__rejectBanner"
          role="status"
          aria-live="polite"
          data-testid="intake-reject-banner"
        >
          <p className="ferro-intake__rejectBannerTitle">
            Reviewer feedback
          </p>
          <p
            className="ferro-intake__rejectBannerReason"
            data-testid="intake-reject-banner-reason"
          >
            {intake.rejectionReason && intake.rejectionReason.length > 0
              ? intake.rejectionReason
              : 'Our team sent notes about this intake. Make an edit to resume where you left off.'}
          </p>
          <p className="ferro-muted">
            Edit any field below to resume. We'll save automatically and
            move this intake back to "in progress" once you start typing.
          </p>
        </aside>
      ) : null}

      <ol className="ferro-intake__steps" aria-label="Intake steps">
        {INTAKE_STEP_IDS.map((s) => {
          const n = stepIdToNumber(s);
          const isActive = s === currentStep;
          const isComplete = n < currentN;
          const isLocked = n > Math.max(intake.lastStep, currentN) + 0;
          return (
            <li
              key={s}
              className={
                'ferro-intake__step'
                + (isActive ? ' ferro-intake__step--active' : '')
                + (isComplete ? ' ferro-intake__step--complete' : '')
                + (isLocked ? ' ferro-intake__step--locked' : '')
              }
              aria-current={isActive ? 'step' : undefined}
            >
              <button
                type="button"
                className="ferro-intake__stepButton"
                onClick={() => goToStep(s)}
                disabled={isLocked}
                data-step={s}
              >
                <span className="ferro-intake__stepIndex">{n}</span>
                <span className="ferro-intake__stepLabel">
                  {INTAKE_STEP_LABELS[s]}
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      <section className="ferro-intake__body">{shellRender}</section>

      <footer className="ferro-intake__footer">
        <div className="ferro-intake__status" aria-live="polite">
          {renderSaveIndicator(saveState)}
        </div>
        <div className="ferro-intake__nav">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={handleBack}
            disabled={atFirst}
          >
            Back
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={handleSaveAndFinishLater}
            data-testid="intake-save-and-finish-later"
          >
            Save &amp; finish later
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={() => void submitCurrentStep()}
            disabled={submitting}
            data-testid={
              atLast ? 'intake-submit-for-review' : 'intake-save-and-continue'
            }
          >
            {submitting
              ? atLast
                ? 'Submitting…'
                : 'Saving…'
              : atLast
                ? 'Submit for Review'
                : 'Save & continue'}
          </button>
        </div>
      </footer>
    </main>
  );
}

function renderSaveIndicator(state: SaveIndicator): ReactNode {
  switch (state.kind) {
    case 'idle':
      return <span className="ferro-muted">Draft not yet saved</span>;
    case 'saving':
      return <span className="ferro-muted">Saving…</span>;
    case 'saved':
      return (
        <span className="ferro-muted">
          Saved {new Date(state.at).toLocaleTimeString()}
        </span>
      );
    case 'offline':
      return (
        <span
          className="ferro-intake__statusOffline"
          data-testid="intake-status-offline"
        >
          Offline — changes will sync when reconnected
        </span>
      );
    case 'error':
      return (
        <span className="ferro-intake__statusError" role="alert">
          {state.message}
        </span>
      );
  }
}

function defaultIsOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  // `navigator.onLine` returns true on most Node/jsdom stubs, matching
  // production browsers' default when there's no offline signal.
  return navigator.onLine !== false;
}

/**
 * Human-friendly reason for an `IntakeSubmitError`. The server's typed
 * `kind` lets us tell the prospect exactly what's wrong: which steps
 * are incomplete, which acknowledgement checkbox is missing, or that
 * the network dropped.
 */
export function describeSubmitError(err: IntakeSubmitError): string {
  switch (err.kind) {
    case 'network':
      return 'We couldn\'t reach the server. Check your connection and try again.';
    case 'illegal_state':
      return 'This intake has already been submitted.';
    case 'incomplete_steps': {
      const missing = err.body?.missing ?? [];
      if (missing.length === 0) {
        return 'Some steps still need attention before you can submit.';
      }
      const labels = missing.map((s) => `Step ${s}`).join(', ');
      return `Finish these before submitting: ${labels}.`;
    }
    case 'step10_invalid':
      return 'Review & submit has missing fields. Scroll up to fix the highlighted items.';
    case 'state_transition_failed':
      return 'The intake couldn\'t be submitted right now. Please try again.';
    case 'db_error':
    case 'unknown':
    default:
      return err.message || 'Submit failed.';
  }
}

function renderSubmittedConfirmation(
  intake: IntakePortalState,
): JSX.Element {
  const submittedLabel =
    intake.submittedAt !== null
      ? new Date(intake.submittedAt).toLocaleString()
      : null;
  return (
    <main
      className="ferro-intake ferro-intake--status"
      aria-label="Intake submitted"
      data-testid="intake-submitted-thankyou"
    >
      <section className="ferro-intake__statusCard">
        <h1 className="ferro-intake__statusTitle">
          Thanks — your intake is with our team
        </h1>
        <p>
          We review every intake within <strong>one business day</strong>. You'll
          get an email at <strong>{intake.inviteEmail}</strong> once the tenant is
          provisioned and your first owner sign-in link is ready.
        </p>
        {submittedLabel !== null ? (
          <p
            className="ferro-muted"
            data-testid="intake-submitted-timestamp"
          >
            Submitted {submittedLabel}
          </p>
        ) : null}
        <p className="ferro-muted">
          This page is now read-only. If something needs to change, reply to
          your invite email and we'll reopen the intake for edits.
        </p>
      </section>
    </main>
  );
}
